import React, { useEffect, useState } from "react";
import Navbar from "components/Navbar/navbar";
import Footer from "components/Footer/footer";
import ProjectHero from "components/Project-hero/project-hero";
import ProjectChallengeSolutionResults from "components/Project-challenge-solution-results/project-challenge-solution-results";
import ProjectArchitecture from "components/Project-architecture/project-architecture";
import ProjectAlloggiatiExtras from "components/Project-alloggiati-extras/project-alloggiati-extras";
import ProjectGallery from "components/Project-gallery/project-gallery";
import ProjectClientTestimonial from "components/Project-client-testimonial/project-client-testimonial";
import DarkTheme from "layouts/Dark";
import SEO from "components/seo/seo.jsx";
import "../pages/work-detail/work-detail.scss";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Pagination, Parallax } from "swiper";

import "swiper/css";
import "swiper/css/pagination";
import { StaticImage } from "gatsby-plugin-image";
import LoadingScreen from "components/Loading-Screen";
import { graphql } from "gatsby";

SwiperCore.use([Pagination, Parallax]);

// Single source of truth for the repeating field groups queried below —
// drives both parsing (parseGroup) and the missing-field report.
const GROUP_CONFIGS = [
  { key: "keyStats", prefix: "stat", maxCount: 4, mapping: { Value: "value", Label: "label", Icon: "icon", CustomIcon: "customIcon" } },
  { key: "challengeCards", prefix: "challengeCard", maxCount: 4, mapping: { Title: "title", Description: "text", Icon: "icon", CustomIcon: "customIcon" } },
  { key: "solutionSteps", prefix: "solutionStep", maxCount: 4, mapping: { Title: "title", Description: "text", Icon: "icon", CustomIcon: "customIcon" } },
  { key: "impactCards", prefix: "impact", maxCount: 4, mapping: { Value: "value", Label: "label", Icon: "icon", CustomIcon: "customIcon" } },
  { key: "roleCards", prefix: "roleCard", maxCount: 4, mapping: { Title: "title", Description: "text", Icon: "icon", CustomIcon: "customIcon" } },
  { key: "screenshots", prefix: "screenshot", maxCount: 4, mapping: { Image: "img", Caption: "title" } },
  { key: "flowSteps", prefix: "flowStep", maxCount: 5, mapping: { Title: "title", Description: "text", Icon: "icon", CustomIcon: "customIcon" } },
];

// Single (non-repeating) fields queried below.
const SINGLE_FIELDS = [
  "heroDescription", "heroImage", "challengeDescription", "solutionDescription",
  "impactDescription", "evolvansRoleDescription", "screenshotsSectionTitle",
  "screenshotsDescription", "flowSectionTitle", "flowDescription",
];

const isFieldEmpty = (val) => {
  if (val === undefined || val === null || val === "") return true;
  // ACF Media Item Edge: { node: { mediaItemUrl } }
  if (typeof val === "object" && "node" in val) return !val.node?.mediaItemUrl;
  return false;
};

// Every field name that has no value in WordPress yet, so it's easy to see
// what still needs content without digging through GraphiQL.
const getMissingCaseStudyFields = (fields) => {
  const missing = [];
  SINGLE_FIELDS.forEach((name) => {
    if (isFieldEmpty(fields[name])) missing.push(name);
  });
  GROUP_CONFIGS.forEach(({ prefix, maxCount, mapping }) => {
    for (let i = 1; i <= maxCount; i++) {
      Object.keys(mapping).forEach((suffix) => {
        const fieldName = `${prefix}${i}${suffix}`;
        if (isFieldEmpty(fields[fieldName])) missing.push(fieldName);
      });
    }
  });
  return missing;
};

const parseGroup = (fields, prefix, maxCount, mapping) => {
  if (!fields) return [];
  const result = [];
  for (let i = 1; i <= maxCount; i++) {
    const item = {};
    let hasData = false;
    Object.keys(mapping).forEach(suffix => {
      const fieldName = `${prefix}${i}${suffix}`;
      let val = fields[fieldName];
      if (val !== undefined && val !== null && val !== "") {
        // Extract URL if it's an ACF Media Item Edge
        if (typeof val === 'object' && val?.node?.mediaItemUrl) {
          val = val.node.mediaItemUrl;
        }
        item[mapping[suffix]] = val;
        hasData = true;
      }
    });
    if (hasData) {
      result.push(item);
    }
  }
  return result;
};

const ProjectDetails2Dark = (props) => {
  const projectData = props.pageContext.node;
  
  // Extract case study data (fallback to first related case study if exists)
  const caseStudyNode = props.data?.wpCaseStudy?.nodes?.[0] || projectData?.caseStudiesData?.nodes?.[0];
  const caseStudyFields = caseStudyNode?.caseStudyFields || {};

  // Parse numbered fields into clean arrays
  const groupedData = {};
  GROUP_CONFIGS.forEach(({ key, prefix, maxCount, mapping }) => {
    groupedData[key] = parseGroup(caseStudyFields, prefix, maxCount, mapping);
  });

  const parsedCaseStudyData = {
    caseStudyTitle: caseStudyNode?.caseStudyTitle,
    heroDescription: caseStudyFields.heroDescription,
    heroImage: caseStudyFields.heroImage?.node?.mediaItemUrl,
    challengeDescription: caseStudyFields.challengeDescription,
    solutionDescription: caseStudyFields.solutionDescription,
    impactDescription: caseStudyFields.impactDescription,
    evolvansRoleDescription: caseStudyFields.evolvansRoleDescription,
    screenshotsSectionTitle: caseStudyFields.screenshotsSectionTitle,
    screenshotsDescription: caseStudyFields.screenshotsDescription,
    flowSectionTitle: caseStudyFields.flowSectionTitle,
    flowDescription: caseStudyFields.flowDescription,
    ...groupedData,
  };

  // Dev-only: list every WP field that's still empty for this case study,
  // so missing content is easy to spot without opening GraphiQL.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const missingFields = getMissingCaseStudyFields(caseStudyNode?.caseStudyFields || {});
    if (missingFields.length > 0) {
      const label = caseStudyNode?.caseStudyTitle || projectData?.projectName || "this case study";
      console.groupCollapsed(
        `%c[Case Study] ${missingFields.length} empty field(s) for "${label}" — fill these in WordPress`,
        "color:#e67e22;font-weight:bold;"
      );
      console.table(missingFields.map((field) => ({ field })));
      console.groupEnd();
    }
  }, [caseStudyNode, projectData]);

  const navbarRef = React.useRef(null);
  const logoRef = React.useRef(null);
  const [theme, setTheme] = useState("Dark");

  React.useEffect(() => {
    var navbar = navbarRef.current;

    if (window.pageYOffset > 300) {
      navbar.classList.add("nav-scroll");
    } else {
      navbar.classList.remove("nav-scroll");
    }

    window.addEventListener("scroll", () => {
      if (window.pageYOffset > 300) {
        navbar.classList.add("nav-scroll");
      } else {
        navbar.classList.remove("nav-scroll");
      }
    });

    var storage = localStorage.getItem("theme");

    if (storage && storage != "") {
      let bodyEl = document.querySelector("body");
      bodyEl.classList.add("themeL");
      setTheme(storage);
    }
  }, [navbarRef]);

  const themeModeChange = (themeMode) => {
    let bodyEl = document.querySelector("body");
    if (themeMode == "Dark") {
      setTheme("themeL");
      bodyEl.classList.add("themeL");
      localStorage.setItem("theme", "themeL");
    } else {
      setTheme("Dark");
      bodyEl.classList.remove("themeL");
      localStorage.removeItem("theme");
    }
  };

  const [load, setLoad] = useState(false);
  const paginationRef = React.useRef(null);

  useEffect(() => {
    setLoad(true);
  }, [load]);

  return (
    <DarkTheme>
      <LoadingScreen/>
      <Navbar
        nr={navbarRef}
        lr={logoRef}
        theme={theme}
        themeModeChange={themeModeChange}
      />
      <div className="wrapper project-detail">
        <ProjectHero projectHeroData={projectData} caseStudyData={parsedCaseStudyData} />
        <ProjectChallengeSolutionResults projectData={projectData} caseStudyData={parsedCaseStudyData} />

        <ProjectAlloggiatiExtras caseStudyData={parsedCaseStudyData} />
        <ProjectGallery projectGalleryData={projectData} />
        {/* <ProjectVideo projectVideoDate={ProjectDate} /> */}



        {projectData &&
          projectData.testimonials?.nodes?.length > 0 &&
          projectData.testimonials.nodes[0].clientName !== "testing" && (
            <section className="app-testim section-padding bg-gray pd-testimonials">
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-7 col-md-10">
                    <div className="s-head text-center mb-80">
                      <h6 className="stit mb-30">
                        <span className="left"></span> Clients Feedback
                        <span className="right"></span>
                      </h6>
                      <h2>Valuable Clients Feedback About Our Services</h2>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <div className="swiper-container">
                      {load ? (
                        <Swiper
                          slidesPerView={1}
                          spaceBetween={30}
                          speed={1000}
                          loop={false}
                          pagination={{
                            clickable: true,
                            el: paginationRef.current,
                          }}
                          className="swiper-wrapper"
                          breakpoints={{
                            320: {
                              slidesPerView: 1,
                              spaceBetween: 0,
                            },
                            640: {
                              slidesPerView: 1,
                              spaceBetween: 0,
                            },
                            767: {
                              slidesPerView: 2,
                              centeredSlides: false,
                            },
                            991: {
                              slidesPerView: 3,
                            },
                          }}
                        >
                          {projectData.testimonials.nodes.map((data, i) => (
                            <SwiperSlide className="swiper-slide" key={i}>
                              <div className="pd-card pd-testi-item">
                                <div className="pd-testi-icon mb-30">
                                  <StaticImage
                                    src="../../static/img/mobile-app/qoute.png"
                                    alt=""
                                  />
                                </div>
                                <div className="pd-testi-text">
                                  <p>{data.clientTestimonial}</p>
                                </div>
                                <div className="pd-testi-info">
                                  <div className="pd-testi-img">
                                    <img
                                      loading="lazy"
                                      src={data.featuredImage?.node?.guid || ""}
                                      alt={data.clientName || "client image"}
                                    />
                                  </div>
                                  <div className="pd-testi-cont">
                                    <h6 className="mb-10">{data.clientName}</h6>
                                    <span>{data.clientDesignation}</span>
                                  </div>
                                </div>
                              </div>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      ) : (
                        ""
                      )}
                      <div ref={paginationRef} className="swiper-pagination"></div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        <Footer />
      </div>
    </DarkTheme>
  );
};

export const Head = (props) => {
  const projectData = props.pageContext.node;
  const strippedDescription = stripHtmlTags(projectData?.projectDescription);
  const featureImageUrl = projectData?.featureImage?.guid || "";
  const shouldhideMetaTags = true;
  const pathname = props.location?.pathname || "";
  const title = `Evolvan - Case Study of ${projectData?.projectName}`;
  const description = strippedDescription || `Evolvan Info Solutions Pvt. Ltd.`;
  const canonicalUrl = pathname ? `https://evolvan.com${pathname}` : "https://evolvan.com/case-studies/";

  const caseStudyJsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: projectData?.projectName,
    description: strippedDescription,
    image: featureImageUrl || undefined,
    url: canonicalUrl,
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://evolvan.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Case Studies",
        item: "https://evolvan.com/case-studies/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: projectData?.projectName,
        item: canonicalUrl,
      },
    ],
  };

    return (
    <>
      <SEO
        title={title}
        description={description}
        pathname={pathname}
        ogtype={`article`}
        ogdescription={strippedDescription}
        ogimage={featureImageUrl} // Pass the featureImage URL
        shouldhideMetaTags={shouldhideMetaTags}
        twittercard={`summary`}
        twitterdescription={strippedDescription}
        twittertitle={title}
        twitterimage={featureImageUrl} // Pass the featureImage URL
        detail={`${projectData?.metaTagDetail}`}
        keywords={`${projectData?.metaTagKeyword}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(caseStudyJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
    </>
  );
};

function stripHtmlTags(htmlString) {
  if (!htmlString) return "";
  return htmlString.replace(/<[^>]+>/g, "");
}

function getLiveUrl() {
  if (typeof window !== "undefined") {
    return window.location.href;
  }
  return "";
}

export default ProjectDetails2Dark;

export const query = graphql`
  query($slug: String!) {
    wpCaseStudy: allWpCaseStudy(
      filter: {caseStudyFields: {parentProject: {nodes: {elemMatch: {slug: {eq: $slug}}}}}}
    ) {
      nodes {

              caseStudyTitle
              challenge
              solution
              results
              caseStudyFields {
                heroDescription
                heroImage { node { mediaItemUrl } }
                parentProject { nodes { slug } }
                stat1Value
                stat1Label
                stat1Icon
                stat1CustomIcon { node { mediaItemUrl } }
                stat2Value
                stat2Label
                stat2Icon
                stat2CustomIcon { node { mediaItemUrl } }
                stat3Value
                stat3Label
                stat3Icon
                stat3CustomIcon { node { mediaItemUrl } }
                stat4Value
                stat4Label
                stat4Icon
                stat4CustomIcon { node { mediaItemUrl } }
                challengeDescription
                challengeCard1Title
                challengeCard1Description
                challengeCard1Icon
                challengeCard1CustomIcon { node { mediaItemUrl } }
                challengeCard2Title
                challengeCard2Description
                challengeCard2Icon
                challengeCard2CustomIcon { node { mediaItemUrl } }
                challengeCard3Title
                challengeCard3Description
                challengeCard3Icon
                challengeCard3CustomIcon { node { mediaItemUrl } }
                challengeCard4Title
                challengeCard4Description
                challengeCard4Icon
                challengeCard4CustomIcon { node { mediaItemUrl } }
                solutionDescription
                solutionStep1Title
                solutionStep1Description
                solutionStep1Icon
                solutionStep1CustomIcon { node { mediaItemUrl } }
                solutionStep2Title
                solutionStep2Description
                solutionStep2Icon
                solutionStep2CustomIcon { node { mediaItemUrl } }
                solutionStep3Title
                solutionStep3Description
                solutionStep3Icon
                solutionStep3CustomIcon { node { mediaItemUrl } }
                solutionStep4Title
                solutionStep4Description
                solutionStep4Icon
                solutionStep4CustomIcon { node { mediaItemUrl } }
                impactDescription
                impact1Value
                impact1Label
                impact1Icon
                impact1CustomIcon { node { mediaItemUrl } }
                impact2Value
                impact2Label
                impact2Icon
                impact2CustomIcon { node { mediaItemUrl } }
                impact3Value
                impact3Label
                impact3Icon
                impact3CustomIcon { node { mediaItemUrl } }
                impact4Value
                impact4Label
                impact4Icon
                impact4CustomIcon { node { mediaItemUrl } }
                evolvansRoleDescription
                roleCard1Title
                roleCard1Description
                roleCard1Icon
                roleCard1CustomIcon { node { mediaItemUrl } }
                roleCard2Title
                roleCard2Description
                roleCard2Icon
                roleCard2CustomIcon { node { mediaItemUrl } }
                roleCard3Title
                roleCard3Description
                roleCard3Icon
                roleCard3CustomIcon { node { mediaItemUrl } }
                roleCard4Title
                roleCard4Description
                roleCard4Icon
                roleCard4CustomIcon { node { mediaItemUrl } }
                screenshotsSectionTitle
                screenshotsDescription
                screenshot1Image { node { mediaItemUrl } }
                screenshot1Caption
                screenshot2Image { node { mediaItemUrl } }
                screenshot2Caption
                screenshot3Image { node { mediaItemUrl } }
                screenshot3Caption
                screenshot4Image { node { mediaItemUrl } }
                screenshot4Caption
                flowSectionTitle
                flowDescription
                flowStep1Title
                flowStep1Description
                flowStep1Icon
                flowStep1CustomIcon { node { mediaItemUrl } }
                flowStep2Title
                flowStep2Description
                flowStep2Icon
                flowStep2CustomIcon { node { mediaItemUrl } }
                flowStep3Title
                flowStep3Description
                flowStep3Icon
                flowStep3CustomIcon { node { mediaItemUrl } }
                flowStep4Title
                flowStep4Description
                flowStep4Icon
                flowStep4CustomIcon { node { mediaItemUrl } }
                flowStep5Title
                flowStep5Description
                flowStep5Icon
                flowStep5CustomIcon { node { mediaItemUrl } }
              }

      }
    }
  }
`;
